import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COVERAGE_BY_SLUG, BEST_TIMES } from '../lib/coverage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LIFE_REASONS = [
  'Protect my family',
  'Mortgage protection',
  'Final expenses',
  'Business protection',
  'Retirement planning',
  'Legacy planning',
  'Not sure yet',
];

const AGE_RANGES = ['Under 30', '30 – 44', '45 – 54', '55 – 64', '65+'];

const BasicLeadForm = () => {
  const { coverageType } = useParams();
  const navigate = useNavigate();
  const meta = COVERAGE_BY_SLUG[coverageType];

  const [data, setData] = useState({
    coverage_type: coverageType,
    first_name: '',
    last_name: '',
    contact_name: '',
    business_name: '',
    business_type: '',
    number_of_vehicles: '',
    vehicle_types: '',
    phone: '',
    email: '',
    zip_code: '',
    property_address: '',
    rental_address: '',
    city: '',
    age_range: '',
    main_reason: '',
    coverage_interest: '',
    turning_65_soon: '',
    already_on_medicare: '',
    best_time_to_contact: '',
    notes: '',
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  if (!meta) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold mb-3" style={{ color: '#01325B' }}>Coverage type not found</h1>
        <Link to="/protection-center" className="text-[#E07A2C] underline">Back to Protection Center</Link>
      </div>
    );
  }

  const isBusiness = coverageType === 'business' || coverageType === 'commercial_auto';
  const isHome = coverageType === 'home';
  const isRenters = coverageType === 'renters';
  const isLife = coverageType === 'life';
  const isMedicare = coverageType === 'medicare';
  const isUmbrella = coverageType === 'umbrella';

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!data.consent) {
      setError('Please confirm consent to be contacted.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...data };
      const res = await axios.post(`${API}/leads`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });
      if (!res?.data?.ok) throw new Error('Submission did not confirm success.');
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Lead submission failed:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      setError(`We could not submit your request right now (${detail}). Please call (972) 292-8640 or email Herecomesprotection@gmail.com and we'll help directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  // ===== CONFIRMATION VIEW =====
  if (result) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-3xl">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg text-center" data-testid="lead-confirmation">
          <div className="text-5xl mb-4">{meta.emoji}</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#01325B' }}>Thank You</h1>
          <p className="text-gray-700 mb-6 text-lg">We received your basic request.</p>
          <p className="text-gray-700 mb-8">
            You can complete your full Protection Profile now for a more accurate quote or coverage review,
            or we can follow up with you using the information you already provided.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => navigate(`/protection-center/profile/${coverageType}?lead_id=${result.lead_id}#t=${result.resume_token}`)}
              data-testid="confirmation-continue-full"
              className="px-6 py-3 rounded-lg font-semibold text-white"
              style={{ background: '#E07A2C' }}
            >
              Complete My Full Protection Profile
            </button>
            <Link
              to="/protection-center"
              data-testid="confirmation-basic-only"
              className="px-6 py-3 rounded-lg font-semibold border-2 inline-flex items-center justify-center"
              style={{ borderColor: '#01325B', color: '#01325B' }}
            >
              Submit Basic Request Only
            </Link>
          </div>
          <p className="text-xs text-gray-500 italic mt-8">
            Your information is used only to help prepare your insurance quote or coverage review. We do not sell your information.
          </p>
          {result.email_prospect_sent === false && (
            <p className="text-xs text-amber-600 mt-3">
              (Note: confirmation email may take a few minutes — your lead is saved.)
            </p>
          )}
        </div>
      </div>
    );
  }

  // ===== FORM =====
  return (
    <div data-testid={`basic-form-${coverageType}`}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'linear-gradient(135deg,#01325B 0%, #02466e 100%)' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <div className="text-5xl mb-3">{meta.emoji}</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{meta.label}</h1>
          <p className="opacity-90 max-w-xl mx-auto">
            Start with the basics. You can complete your full Protection Profile when ready.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 bg-[#f6f5f1]">
        <div className="container mx-auto px-4 max-w-3xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-6">
            <div className="text-sm text-gray-600 italic">
              Estimated time: under 1 minute · Required fields marked with *
            </div>

            {/* Name fields — business uses contact_name + business_name */}
            {isBusiness ? (
              <>
                <Field label="Business Name *" testid="field-business_name">
                  <input
                    required
                    value={data.business_name}
                    onChange={(e) => update('business_name', e.target.value)}
                    className={inputCls}
                    placeholder="ABC Corp"
                  />
                </Field>
                <Field label="Contact Name *" testid="field-contact_name">
                  <input
                    required
                    value={data.contact_name}
                    onChange={(e) => update('contact_name', e.target.value)}
                    className={inputCls}
                    placeholder="Your name"
                  />
                </Field>
              </>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First Name *" testid="field-first_name">
                  <input
                    required
                    value={data.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                    className={inputCls}
                    placeholder="First"
                  />
                </Field>
                <Field label="Last Name *" testid="field-last_name">
                  <input
                    required
                    value={data.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                    className={inputCls}
                    placeholder="Last"
                  />
                </Field>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone Number *" testid="field-phone">
                <input
                  required
                  type="tel"
                  value={data.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className={inputCls}
                  placeholder="(555) 123-4567"
                />
              </Field>
              <Field label="Email Address *" testid="field-email">
                <input
                  required
                  type="email"
                  value={data.email}
                  onChange={(e) => update('email', e.target.value)}
                  className={inputCls}
                  placeholder="you@example.com"
                />
              </Field>
            </div>

            {/* Coverage-specific basics */}
            {coverageType === 'business' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Type of Business *" testid="field-business_type">
                  <input
                    required
                    value={data.business_type}
                    onChange={(e) => update('business_type', e.target.value)}
                    className={inputCls}
                    placeholder="e.g. Landscaping"
                  />
                </Field>
                <Field label="City *" testid="field-city">
                  <input
                    required
                    value={data.city}
                    onChange={(e) => update('city', e.target.value)}
                    className={inputCls}
                    placeholder="Dallas"
                  />
                </Field>
              </div>
            )}

            {coverageType === 'commercial_auto' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Number of Vehicles *" testid="field-num_vehicles">
                  <input
                    required
                    value={data.number_of_vehicles}
                    onChange={(e) => update('number_of_vehicles', e.target.value)}
                    className={inputCls}
                    placeholder="3"
                  />
                </Field>
                <Field label="Type of Vehicles *" testid="field-vehicle_types">
                  <input
                    required
                    value={data.vehicle_types}
                    onChange={(e) => update('vehicle_types', e.target.value)}
                    className={inputCls}
                    placeholder="Trucks, vans"
                  />
                </Field>
              </div>
            )}

            {isHome && (
              <Field label="Property Address *" testid="field-property_address">
                <input
                  required
                  value={data.property_address}
                  onChange={(e) => update('property_address', e.target.value)}
                  className={inputCls}
                  placeholder="123 Main St, Dallas, TX"
                />
              </Field>
            )}

            {isRenters && (
              <Field label="Rental Address *" testid="field-rental_address">
                <input
                  required
                  value={data.rental_address}
                  onChange={(e) => update('rental_address', e.target.value)}
                  className={inputCls}
                  placeholder="123 Main St, Apt 4, Dallas, TX"
                />
              </Field>
            )}

            {/* ZIP — required for most */}
            {coverageType !== 'commercial_auto' && (
              <Field label={`ZIP Code ${isLife ? '' : '*'}`} testid="field-zip_code">
                <input
                  required={!isLife}
                  value={data.zip_code}
                  onChange={(e) => update('zip_code', e.target.value)}
                  className={inputCls}
                  placeholder="75001"
                />
              </Field>
            )}

            {/* Life-specific */}
            {isLife && (
              <>
                <Field label="Age Range *" testid="field-age_range">
                  <select
                    required
                    value={data.age_range}
                    onChange={(e) => update('age_range', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select...</option>
                    {AGE_RANGES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
                <Field label="Main Reason for Coverage *" testid="field-main_reason">
                  <select
                    required
                    value={data.main_reason}
                    onChange={(e) => update('main_reason', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select...</option>
                    {LIFE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </>
            )}

            {/* Umbrella-specific */}
            {isUmbrella && (
              <Field label="Coverage Interest *" testid="field-coverage_interest">
                <select
                  required
                  value={data.coverage_interest}
                  onChange={(e) => update('coverage_interest', e.target.value)}
                  className={inputCls}
                >
                  <option value="">Select...</option>
                  <option value="$1M">$1,000,000</option>
                  <option value="$2M">$2,000,000</option>
                  <option value="$3M+">$3,000,000+</option>
                  <option value="not_sure">Not sure yet</option>
                </select>
              </Field>
            )}

            {/* Medicare-specific */}
            {isMedicare && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Turning 65 soon? *" testid="field-turning_65">
                  <select
                    required
                    value={data.turning_65_soon}
                    onChange={(e) => update('turning_65_soon', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </Field>
                <Field label="Already on Medicare? *" testid="field-already_medicare">
                  <select
                    required
                    value={data.already_on_medicare}
                    onChange={(e) => update('already_on_medicare', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </Field>
              </div>
            )}

            <Field label="Best Time to Contact *" testid="field-best_time">
              <select
                required
                value={data.best_time_to_contact}
                onChange={(e) => update('best_time_to_contact', e.target.value)}
                className={inputCls}
              >
                <option value="">Select...</option>
                {BEST_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            <Field label="Optional Notes" testid="field-notes">
              <textarea
                rows="3"
                value={data.notes}
                onChange={(e) => update('notes', e.target.value)}
                className={inputCls}
                placeholder="Anything you'd like us to know..."
              />
            </Field>

            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={data.consent}
                onChange={(e) => update('consent', e.target.checked)}
                className="mt-1"
                data-testid="field-consent"
              />
              <span>
                I consent to be contacted by Here Comes Protection via phone, email, or text regarding my insurance needs.
                Your information is used only to help prepare your insurance quote or coverage review. We do not sell your information. *
              </span>
            </label>

            {error && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-300 text-red-800 text-sm" data-testid="lead-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-btn"
              className="w-full py-4 rounded-lg font-bold text-white text-base shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-transform hover:scale-[1.01]"
              style={{ background: '#E07A2C' }}
            >
              {submitting ? 'Submitting...' : 'Submit Basic Request'}
            </button>

            <p className="text-xs text-gray-500 text-center italic">
              After this step, you'll get the option to continue to your full Protection Profile or finish later.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

const inputCls = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E07A2C] focus:border-transparent outline-none text-sm';

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    {children}
  </div>
);

export default BasicLeadForm;
